import React, { useState } from 'react';
// import { loadStripe } from '@stripe/stripe-js';
// import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createDepositIntent, getPaymentLimits } from '../services/api';
import './DepositModal.css';

// Mock Checkout Form (Replaces Stripe Elements for Demo)
function MockCheckoutForm({ amount, onSuccess, onCancel }) {
  const [processing, setProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    // Simulate network request
    setTimeout(() => {
      setProcessing(false);
      onSuccess();
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit} className="deposit-form mock-checkout">
      <div className="form-row">
        <label>Card Number</label>
        <input 
          type="text" 
          placeholder="0000 0000 0000 0000" 
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          className="mock-input"
          required
        />
      </div>
      <div className="form-row split">
        <div className="half">
          <label>Expiry</label>
          <input 
            type="text" 
            placeholder="MM/YY" 
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            className="mock-input"
            required
          />
        </div>
        <div className="half">
          <label>CVC</label>
          <input 
            type="text" 
            placeholder="123" 
            value={cvc}
            onChange={(e) => setCvc(e.target.value)}
            className="mock-input"
            required
          />
        </div>
      </div>

      <div className="deposit-actions">
        <button
          type="button"
          className="deposit-cancel-btn"
          onClick={onCancel}
          disabled={processing}
        >
          Back
        </button>
        <button
          type="submit"
          className="deposit-submit-btn"
          disabled={processing}
        >
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

function DepositModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limits, setLimits] = useState({ min: 10, max: 10000 });

  React.useEffect(() => {
    loadLimits();
  }, []);

  const loadLimits = async () => {
    try {
      const response = await getPaymentLimits();
      if (response.success) {
        setLimits({
          min: response.deposit.min,
          max: response.deposit.max
        });
      }
    } catch (err) {
      console.error('Error loading limits:', err);
    }
  };

  const quickAmounts = [25, 50, 100, 250, 500];

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  const handleContinue = async () => {
    const depositAmount = parseFloat(amount);

    if (!depositAmount || depositAmount < limits.min) {
      setError(`Minimum deposit is $${limits.min}`);
      return;
    }

    if (depositAmount > limits.max) {
      setError(`Maximum deposit is $${limits.max}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createDepositIntent(depositAmount);
      if (response.success) {
        setClientSecret(response.clientSecret);
        setStep(2);
      } else {
        setError(response.message || 'Failed to create deposit');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create deposit');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content deposit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        {step === 1 && (
          <>
            <h2>Deposit Funds</h2>
            <p className="deposit-subtitle">Choose an amount to add to your balance</p>

            <div className="quick-amounts">
              {quickAmounts.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`quick-amount-btn ${amount === value.toString() ? 'active' : ''}`}
                  onClick={() => handleQuickAmount(value)}
                >
                  ${value}
                </button>
              ))}
            </div>

            <div className="custom-amount">
              <label htmlFor="amount">Custom Amount</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  id="amount"
                  type="number"
                  min={limits.min}
                  max={limits.max}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`${limits.min} - ${limits.max}`}
                  className="amount-input"
                />
              </div>
              <small className="amount-hint">
                Min: ${limits.min} | Max: ${limits.max.toLocaleString()}
              </small>
            </div>

            {error && (
              <div className="deposit-error">
                {error}
              </div>
            )}

            <div className="deposit-actions">
              <button
                type="button"
                className="deposit-cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="deposit-continue-btn"
                onClick={handleContinue}
                disabled={!amount || loading}
              >
                {loading ? 'Loading...' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 2 && clientSecret && (
          <>
            <h2>Complete Payment</h2>
            <p className="deposit-subtitle">
              Depositing ${parseFloat(amount).toFixed(2)}
            </p>
            
            {/* Swapped Stripe Elements for Mock Form */}
            <MockCheckoutForm
              amount={parseFloat(amount)}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setStep(1)}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default DepositModal;
